// Télémétrie des uploads vidéo (TUS)
//
// Les échecs d'upload n'arrivaient jamais dans Sentry : les callbacks TUS se
// contentaient d'un console.error. Et le cas le plus courant sur mobile —
// l'élève quitte l'app ou verrouille l'écran pendant l'envoi, Android gèle puis
// tue la page — ne déclenche même pas onError : l'upload disparaît sans trace,
// le backend finit par marquer la vidéo FAILED (« Raw file not found in
// storage »). Ce module couvre les deux cas :
//
//   1. reportUploadFailure() — échec explicite (placeholder, TUS, confirmation),
//      envoyé à Sentry avec le contexte fichier / progression / réseau /
//      visibilité de la page.
//   2. trackUploadStart / Progress / End() — registre des uploads en cours dans
//      localStorage. reportAbandonedUploads(), appelé au démarrage de l'app,
//      signale ceux qu'un chargement précédent n'a jamais terminés.
//
// Tout accès à localStorage est best-effort (mode privé, quota).

import * as Sentry from '@sentry/react';

const KEY = 'kaiylo:inflight-uploads';
// Battement de cœur tant qu'un upload tourne dans cet onglet.
const HEARTBEAT_MS = 10 * 1000;
// Une entrée dont le dernier battement est plus récent que ça appartient
// peut-être à un autre onglet encore vivant : on la laisse tranquille.
const STALE_AFTER_MS = 45 * 1000;
// Au-delà, l'entrée est trop vieille pour être utile : on la jette sans rapport.
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
// onProgress tire des dizaines de fois par seconde : on n'écrit pas à chaque fois.
const PROGRESS_WRITE_MS = 5 * 1000;

const live = new Map(); // id -> enregistrement des uploads de CET onglet
let heartbeat = null;
let lastPersistAt = 0;

const readAll = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const data = raw ? JSON.parse(raw) : {};
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
};

const writeAll = (all) => {
  try {
    if (Object.keys(all).length) localStorage.setItem(KEY, JSON.stringify(all));
    else localStorage.removeItem(KEY);
  } catch {
    // best-effort
  }
};

const persist = () => {
  const now = Date.now();
  const all = readAll();
  for (const [id, rec] of live) all[id] = { ...rec, lastSeenAt: now };
  writeAll(all);
  lastPersistAt = now;
};

const syncHeartbeat = () => {
  if (live.size && !heartbeat) {
    heartbeat = setInterval(persist, HEARTBEAT_MS);
  } else if (!live.size && heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
};

const currentUserId = () => {
  try {
    return Sentry.getIsolationScope?.()?.getUser?.()?.id
      || Sentry.getCurrentScope?.()?.getUser?.()?.id
      || null;
  } catch {
    return null;
  }
};

const environment = () => {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const conn = nav?.connection;
  return {
    online: nav ? nav.onLine : null,
    visibility: typeof document !== 'undefined' ? document.visibilityState : null,
    connectionType: conn?.effectiveType ?? null,
    downlinkMbps: conn?.downlink ?? null,
    rttMs: conn?.rtt ?? null,
    saveData: conn?.saveData ?? null,
  };
};

const truncate = (value, max = 500) => {
  if (value == null) return null;
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  return str && str.length > max ? `${str.slice(0, max)}…` : str;
};

// Erreur axios (placeholder / confirmation) ou DetailedError de tus-js-client.
const describeError = (err) => {
  const out = {
    name: err?.name ?? null,
    message: err?.message ?? String(err),
    code: err?.code ?? null,
    httpStatus: err?.response?.status ?? null,
    responseBody: truncate(err?.response?.data),
  };
  try {
    const res = err?.originalResponse;
    if (res) {
      out.httpStatus = res.getStatus();
      out.responseBody = truncate(res.getBody());
    }
    const req = err?.originalRequest;
    if (req) out.requestMethod = req.getMethod();
    if (err?.causingError) out.causingError = String(err.causingError?.message || err.causingError);
  } catch {
    // les accesseurs tus peuvent lever sur une requête avortée
  }
  return out;
};

// Vue lisible d'un enregistrement, figée à l'instant `at`.
const snapshot = (rec, at) => {
  const hiddenMs = (rec.hiddenMs || 0) + (rec.hiddenSince ? Math.max(0, at - rec.hiddenSince) : 0);
  return {
    kind: rec.kind,
    videoId: rec.videoId ?? null,
    isRetry: !!rec.isRetry,
    fileSizeMB: typeof rec.fileSize === 'number' ? Math.round((rec.fileSize / 1048576) * 10) / 10 : null,
    fileType: rec.fileType ?? null,
    progressPct: rec.fileSize ? Math.round(((rec.bytesUploaded || 0) / rec.fileSize) * 100) : null,
    elapsedSec: Math.round((at - rec.startedAt) / 1000),
    secSinceLastProgress: Math.round((at - (rec.lastProgressAt || rec.startedAt)) / 1000),
    hiddenCount: rec.hiddenCount || 0,
    hiddenSec: Math.round(hiddenMs / 1000),
  };
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!live.size) return;
    const now = Date.now();
    const hidden = document.visibilityState === 'hidden';
    for (const rec of live.values()) {
      if (hidden) {
        rec.hiddenCount = (rec.hiddenCount || 0) + 1;
        rec.hiddenSince = now;
      } else if (rec.hiddenSince) {
        rec.hiddenMs = (rec.hiddenMs || 0) + (now - rec.hiddenSince);
        rec.hiddenSince = null;
      }
    }
    try {
      Sentry.addBreadcrumb({
        category: 'video_upload',
        level: 'info',
        message: hidden ? 'Page hidden during upload' : 'Page visible again during upload',
        data: { activeUploads: live.size },
      });
    } catch {
      // ignore
    }
    // Écrire tout de suite : si Android tue la page en arrière-plan, c'est le
    // dernier état que le prochain chargement retrouvera.
    persist();
  });
}

/**
 * Enregistre un upload qui démarre (après création du placeholder).
 * `id` = clé de l'upload côté appelant (réutilisée par Progress/End/Failure).
 *
 * @param {string} id
 * @param {{ kind?: string, videoId?: string | null, file?: { size?: number, type?: string } | null, isRetry?: boolean }} [info]
 */
export function trackUploadStart(id, { kind, videoId, file, isRetry = false } = {}) {
  const now = Date.now();
  live.set(id, {
    kind: kind || 'unknown',
    videoId: videoId ?? null,
    fileSize: file?.size ?? null,
    fileType: file?.type ?? null,
    isRetry,
    userId: currentUserId(),
    startedAt: now,
    bytesUploaded: 0,
    lastProgressAt: now,
    hiddenCount: 0,
    hiddenMs: 0,
    hiddenSince: typeof document !== 'undefined' && document.visibilityState === 'hidden' ? now : null,
  });
  persist();
  syncHeartbeat();
}

export function trackUploadProgress(id, bytesUploaded) {
  const rec = live.get(id);
  if (!rec) return;
  const now = Date.now();
  rec.bytesUploaded = bytesUploaded;
  rec.lastProgressAt = now;
  if (now - lastPersistAt >= PROGRESS_WRITE_MS) persist();
}

/** Nombre d'uploads en cours dans cet onglet (contexte des rapports de blocage). */
export function getLiveUploadCount() {
  return live.size;
}

/** Upload terminé (succès) ou abandonné volontairement (annulation, fermeture). */
export function trackUploadEnd(id) {
  live.delete(id);
  const all = readAll();
  if (id in all) {
    delete all[id];
    writeAll(all);
  }
  syncHeartbeat();
}

/**
 * Échec explicite d'un upload. `stage` : 'placeholder' | 'tus' | 'confirm'.
 * `info` complète le contexte quand l'upload n'est pas (encore) enregistré,
 * typiquement un échec du placeholder : { kind, file, videoId }.
 *
 * @param {string | null} id
 * @param {'placeholder' | 'tus' | 'confirm'} stage
 * @param {unknown} err
 * @param {{ kind?: string, videoId?: string | null, file?: { size?: number, type?: string } | null }} [info]
 */
export function reportUploadFailure(id, stage, err, info = {}) {
  const rec = id != null ? live.get(id) : null;
  const now = Date.now();
  const kind = rec?.kind || info.kind || 'unknown';
  const details = describeError(err);
  const context = rec
    ? snapshot(rec, now)
    : {
      kind,
      videoId: info.videoId ?? null,
      fileSizeMB: typeof info.file?.size === 'number' ? Math.round((info.file.size / 1048576) * 10) / 10 : null,
      fileType: info.file?.type ?? null,
    };

  try {
    // Message fixe : le beforeSend de main.jsx jette les événements dont le
    // message contient « Network Error » ou « canceled » — précisément les
    // échecs réseau qu'on veut voir. Le message d'origine part dans `extra`.
    Sentry.captureException(new Error(`Video upload failed (${stage})`), {
      level: 'error',
      fingerprint: ['video-upload-failed', kind, stage],
      tags: {
        feature: 'video_upload',
        upload_kind: kind,
        upload_stage: stage,
        http_status: String(details.httpStatus ?? 'none'),
      },
      extra: { ...context, error: details, ...environment() },
    });
  } catch {
    // un rapport raté ne doit pas casser le flux d'upload
  }

  if (id != null) trackUploadEnd(id);
}

/**
 * À appeler au démarrage de l'app. Signale les uploads qu'un chargement
 * précédent a laissés en cours (page tuée / fermée pendant l'envoi).
 * Retourne le nombre d'uploads signalés.
 */
export function reportAbandonedUploads() {
  const all = readAll();
  const now = Date.now();
  let reported = 0;
  let changed = false;

  for (const [id, rec] of Object.entries(all)) {
    if (live.has(id) || !rec || typeof rec !== 'object') continue;
    const lastSeen = typeof rec.lastSeenAt === 'number' ? rec.lastSeenAt : 0;
    if (now - lastSeen < STALE_AFTER_MS) continue;

    delete all[id];
    changed = true;
    if (typeof rec.startedAt !== 'number' || now - rec.startedAt > MAX_AGE_MS) continue;

    const context = snapshot(rec, lastSeen);
    try {
      Sentry.captureMessage('Video upload abandoned before completion', {
        level: 'warning',
        fingerprint: ['video-upload-abandoned', context.kind || 'unknown'],
        user: rec.userId ? { id: rec.userId } : undefined,
        tags: {
          feature: 'video_upload',
          upload_kind: context.kind || 'unknown',
          upload_stage: 'abandoned',
          hidden_when_lost: String(!!rec.hiddenSince),
        },
        extra: {
          ...context,
          hiddenWhenLost: !!rec.hiddenSince,
          lostAt: new Date(lastSeen).toISOString(),
          reportedAfterSec: Math.round((now - lastSeen) / 1000),
        },
      });
      reported += 1;
    } catch {
      // un rapport raté ne doit pas casser le démarrage
    }
  }

  if (changed) writeAll(all);
  return reported;
}
